<template>
  <RevCard class="p-24">
    <div v-for="item in formattedItems" :key="item.id" class="pt-24 first:pt-0">
      <div class="flex" data-qa="product">
        <div class="relative">
          <div
            class="border-static-default-low rounded-sm flex h-40 w-40 items-center justify-center border"
          >
            <RevIllustration
              :alt="item.title"
              class="h-32 w-32"
              :height="32"
              :src="item.image"
              :width="32"
            />
          </div>
          <div
            v-if="item.quantity"
            class="bg-static-default-low border-static-default-low text-static-default-low shadow-long caption absolute -right-8 -top-8 rounded-[6px] border px-[2px]"
          >
            {{ item.quantity }}
          </div>
        </div>
        <div class="ml-24 w-full">
          <div class="flex justify-between">
            <h2 class="body-2-bold" data-qa="product-name">
              {{ item.title }}
            </h2>
            <div class="body-2 ml-24" data-qa="product-price">
              {{ item.price }}
            </div>
          </div>
          <div class="body-2 mt-8 flex justify-between" data-qa="shipping">
            <div>{{ i18n(translations.shipping) }}</div>
            <div class="ml-24">{{ item.shippingPrice }}</div>
          </div>
          <div
            v-if="item.insurance"
            class="body-2 mt-8 flex justify-between"
            data-qa="insurance"
          >
            <div>{{ item.insurance.title }}</div>
            <div class="ml-24">
              {{ i18n.price(item.insurance.price) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="withSwap" class="pt-24" data-qa="swap-offer">
      <div class="flex">
        <div
          class="border-static-default-low rounded-sm flex h-48 w-48 items-center justify-center border"
        >
          <div class="flex p-12"><IconSwap /></div>
        </div>
        <div class="ml-16 w-full">
          <div class="flex justify-between">
            <h2 class="body-2-bold">{{ i18n(translations.swap) }}</h2>
            <div class="text-action-success-hi body-2 ml-24">
              {{ swapOffer?.price ? i18n.price(swapOffer?.price) : '' }}
            </div>
          </div>
          <div class="body-2 mt-8 flex justify-between">
            <div>{{ swapOffer?.title }}</div>
          </div>
        </div>
      </div>
    </div>

    <RevDivider class="my-16" />

    <div class="body-2 flex justify-between">
      <div>{{ i18n(translations.subtotal) }}</div>
      <div class="ml-24">
        {{ subtotal ? i18n.price(subtotal) : '' }}
      </div>
    </div>

    <div v-if="withServiceFee" class="mt-8 flex justify-between">
      <ServiceFeeModal />
      <div class="body-2 ml-24" data-qa="service-fee">
        {{ serviceFee ? i18n.price(serviceFee) : '' }}
      </div>
    </div>

    <div v-if="shouldDisplayTax" class="body-2 mt-8 flex justify-between">
      <div>{{ taxTitle }}</div>
      <div class="ml-24" data-qa="tax">
        {{ taxPrice }}
      </div>
    </div>

    <div v-if="withDiscount" class="mt-8 flex justify-between">
      <RevTag class="uppercase" :label="discountToken" variant="info" />
      <div class="text-action-success-hi body-2 ml-24" data-qa="discount">
        -{{ discountPrice ? i18n.price(discountPrice) : '' }}
      </div>
    </div>

    <div v-if="selectedPaymentMethod">
      <InstallmentSimulation
        :base-price="priceAfterDiscount"
        data-qa="installments"
        :payment-method="selectedPaymentMethod as PaymentMethod"
      >
        <template #installments="{ isLoading, simulation }">
          <div v-if="!isLoading">
            <div class="body-2 mt-8 flex justify-between">
              <div class="flex">
                <div>{{ i18n(translations.installment) }}</div>
                <PaymentIcons
                  class="ml-1"
                  :methods="[selectedPaymentMethod as PaymentMethod]"
                />
              </div>
              <div class="ml-24">
                {{ i18n.price(simulation?.cost || '') }}
              </div>
            </div>

            <RevDivider class="my-6" />

            <div class="body-1-bold mt-8 flex justify-between">
              <div>{{ totalTitle }}</div>
              <div class="ml-24">
                {{ i18n.price(simulation?.totalAmount || '') }}
              </div>
            </div>
          </div>
        </template>
        <template #fallback>
          <RevDivider class="my-6" />

          <div class="body-1-bold mt-8 flex justify-between">
            <div>{{ totalTitle }}</div>
            <div class="ml-24" data-qa="price-after-discount">
              {{ i18n.price(priceAfterDiscount) }}
            </div>
          </div>
        </template>
      </InstallmentSimulation>
    </div>

    <div v-if="!selectedPaymentMethod">
      <RevDivider class="my-16" />

      <div class="body-1-bold mt-8 flex justify-between">
        <div>{{ totalTitle }}</div>
        <div class="ml-24" data-qa="price-after-discount">
          {{ i18n.price(priceAfterDiscount) }}
        </div>
      </div>
    </div>

    <div id="checkout-side-cart-submit" />

    <CheckoutLegalLinks :with-cancel="withExtendedLegal" />

    <PaymentIcons class="mt-32" />

    <PaymentAdvertisement
      v-if="withPaymentAvailability"
      :base-price="priceAfterDiscount"
      class="mt-24 flex"
      spot="cart"
    />
  </RevCard>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed } from 'vue'

import type {
  BuybackOffer,
  MonetaryAmount,
  cartAPI,
} from '@backmarket/http-api'
import { type PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevCard } from '@ds/components/Card'
import { RevDivider } from '@ds/components/Divider'
import { RevIllustration } from '@ds/components/Illustration'
import { RevTag } from '@ds/components/Tag'
import { IconSwap } from '@ds/icons/IconSwap'

import { PaymentAdvertisement } from '~/scopes/payment'
import InstallmentSimulation from '~/scopes/payment/scopes/advertising/components/InstallmentSimulation'
import PaymentIcons from '~/scopes/payment/scopes/methods/components/PaymentIcons'

import { CHECKOUT } from '../routes-names'

import CheckoutLegalLinks from './CheckoutLegalLinks.vue'
import translations from './CheckoutSummaryContent.translations'
import ServiceFeeModal from './ServiceFeeModal.vue'

const i18n = useI18n()
const route = useRoute()

const props = withDefaults(
  defineProps<{
    discountPrice?: MonetaryAmount
    discountToken?: string
    items: cartAPI.CartItem[]
    priceAfterDiscount: MonetaryAmount
    selectedPaymentMethod?: PaymentMethod | null
    serviceFee?: MonetaryAmount | null
    subtotal?: MonetaryAmount
    swapOffer?: BuybackOffer | null
    tax?: MonetaryAmount | null
    withDiscount: boolean
    withExtendedLegal: boolean
    withPaymentAvailability: boolean
    withServiceFee: boolean
    withSwap: boolean
    withTax: boolean | null
  }>(),
  {
    discountPrice: undefined,
    discountToken: '',
    selectedPaymentMethod: undefined,
    serviceFee: undefined,
    swapOffer: undefined,
    subtotal: undefined,
    tax: null,
  },
)

const formattedItems = computed(() =>
  props.items.map((item) => {
    let quantity = ''

    if (item.quantity > 10) {
      quantity = '+10'
    } else if (item.quantity > 1) {
      quantity = `${item.quantity}`
    }

    return {
      id: item.listingId,
      image: item.image,
      price: i18n.price(item.price),
      shippingPrice:
        item.shippingSelected.sumShippingFeePrice === '0'
          ? i18n(translations.free)
          : i18n.price(item.shippingSelected.sumShippingFeePrice),
      title: item.title,
      insurance: item.insuranceOffers?.find(
        (offer) => offer.selected && !offer.defaultOffer,
      ),
      quantity,
    }
  }),
)

const shouldDisplayTax = computed(() => !props.withTax || !isEmpty(props.tax))

const taxTitle = computed(() =>
  route.name === CHECKOUT.PAYMENT
    ? i18n(translations.tax)
    : i18n(translations.estimatedTax),
)

const taxPrice = computed(() =>
  isEmpty(props.tax) ? i18n(translations.pending) : i18n.price(props.tax),
)

const totalTitle = computed(() =>
  shouldDisplayTax.value
    ? i18n(translations.total)
    : i18n(translations.totalWithTaxes),
)
</script>
